import { Timestamp } from "firebase/firestore";
import { HeadProps } from "gatsby";
import { IGatsbyImageData } from "gatsby-plugin-image";

export enum AdjustmentType {
  DiscountPercentage = "discountPercentage",
  DiscountValue = "discountValue",
  IncreasePercentage = "increasePercentage",
  IncreaseValue = "increaseValue",
  None = "",
}

export interface FileObject {
  file: File;
  language: string;
}

export interface ImageUrlObject {
  language: string;
  originalName: string;
  uploadTime: number;
  size: number;
  type: string;
}

export interface PatternData {
  id?: string;
  patternName: {
    en: string;
    pt: string;
  };
  description: {
    en: string;
    pt: string;
  };
  materials: {
    en: string;
    pt: string;
  };
  category: string;
  initialPrice: {
    usd: number;
    brl: number;
  };
  adjustmentType: AdjustmentType;
  adjustmentAmount: number;
  price: {
    usd: number;
    brl: number;
  };
  mainImage: string | undefined;
  secondaryImages: string[];
  localMainImage: GatsbyImageData;
  localSecondaryImages: GatsbyImageData[];
  uploadDate: Date;
  files: ImageUrlObject[];
  feedbacks?: FeedbackData[];
  packPatternIds?: string[];
}

export interface FeedbackData {
  creationDate: Timestamp;
  imageUrl: string;
  review: string;
}

export interface UserData {
  email: string;
  displayName?: string;
  photoURL?: string;
  nationality?: string;
  country?: string;
  otherLanguage?: string;
  personalId?: string;
  birthDate?: string;
  preferredLanguage?: string;
}

export interface PromoCodeData {
  promoCode: string;
  isThereMinNumber: boolean;
  listingsOption: "all" | "select";
  selectedListings: { [key: string]: boolean };
  discountType: "PercentageOff" | "FixedAmountOff";
  discountValue: string;
  minNumberOfItems: string;
  startDate: string;
  endDate: string;
}

export type CurrencyType = "USD" | "BRL";

export interface PurchasedPattern {
  patternId?: string;
  name: string;
  purchaseDate: Date;
  amount: number;
  currency: "usd" | "brl";
  orderId: string;
  files: ImageUrlObject[];
  mainImage: string | undefined;
  paymentStatus: string;
}

export interface SEOProps {
  title?: string;
  description?: string;
  imageUrl?: string;
  pathname?: string;
}

interface LocaleNode {
  ns: string;
  data: string;
  language: string;
}

interface LocaleEdge {
  node: LocaleNode;
}

interface LocalesData {
  locales: {
    edges: LocaleEdge[];
  };
}

export interface HeadPropsWithData extends HeadProps {
  data: LocalesData;
}

export interface PatternHeadPropsWithData extends HeadPropsWithData {
  pageContext: {
    pattern: {
      patternName: {
        en: string;
        [key: string]: string;
      };
      description: {
        en: string;
        [key: string]: string;
      };
      id: string;
      localMainImage?: {
        childImageSharp?: {
          gatsbyImageData?: {
            images?: {
              fallback?: {
                src: string;
              };
            };
          };
        };
      };
      price: {
        usd: number;
        [key: string]: number;
      };
      category: string;
    };
  };
}

export interface CartItem {
  id: string;
  name: string;
  price: {
    usd: number;
    brl: number;
  };
  thumbnail: IGatsbyImageData;
}

export interface DiscountInfo {
  isValid: boolean;
  discountAmount: number;
  errorMessage?: string;
}

export interface GatsbyImageData {
  childImageSharp: {
    gatsbyImageData: IGatsbyImageData;
  };
}

export interface CategoryData {
  id: string;
  name: string;
  namePT: string;
}

export interface YoutubeVideo {
  id: string;
  title: string;
  description: string;
  publishedAt: string;
  thumbnail: string;
  type: string;
  playlistId: string;
  playlistName?: string;
}

export interface YoutubeVideosQueryResult {
  allYoutubeVideo: {
    nodes: YoutubeVideo[];
  };
}
